/**
 * CertSelectPage — FR-02 種別選択
 * 進捗は種別ごと別管理。初回登録後・ヘッダーからいつでも切替可能。
 */

import { useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import type { CertificationType } from '../api/types'
import { updateCertType } from '../api'
import { ApiClientError } from '../api/client'
import { AuthContext } from '../contexts/AuthContext'
import Spinner from '../components/Spinner'

const CERT_OPTIONS: { value: CertificationType; label: string; desc: string }[] = [
  {
    value: 'first_class',
    label: '第一種衛生管理者',
    desc: '全業種対象 / 出題範囲：広い',
  },
  {
    value: 'second_class',
    label: '第二種衛生管理者',
    desc: '有害業務以外 / 出題範囲：標準',
  },
]

export default function CertSelectPage() {
  const { user, setUser } = useContext(AuthContext)
  const navigate = useNavigate()
  const [selected, setSelected] = useState<CertificationType>(
    user?.current_cert_type ?? 'first_class',
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = async () => {
    setError(null)
    setLoading(true)
    try {
      const updated = await updateCertType({ certification_type: selected })
      setUser(updated)
      navigate('/', { replace: true })
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message)
      } else {
        setError('種別の設定に失敗しました')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-base px-6 py-10">
      <div className="w-full max-w-[400px]">
        <h1 className="mb-1.5 text-[22px] font-bold tracking-tight text-text-primary">
          学習する種別を選択
        </h1>
        <p className="mb-6 text-sm text-text-secondary">いつでも切替できます</p>

        {error && (
          <div role="alert" className="alert-error mb-4">
            {error}
          </div>
        )}

        <div className="mb-7 flex flex-col gap-3">
          {CERT_OPTIONS.map((opt) => {
            const isSelected = selected === opt.value
            return (
              <button
                key={opt.value}
                data-testid={`select-${opt.value.replace('_', '-')}`}
                onClick={() => setSelected(opt.value)}
                aria-pressed={isSelected}
                className={[
                  'flex items-start gap-3.5 rounded-lg p-4 text-left shadow-card transition-colors duration-fast ease-smooth',
                  isSelected
                    ? 'border-2 border-accent bg-accent-subtle'
                    : 'border border-border-strong bg-surface hover:border-accent',
                ].join(' ')}
              >
                {/* ラジオ形状 (#63 色のみに依存しない) */}
                <span
                  className={[
                    'mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full border-2',
                    isSelected ? 'border-accent bg-accent' : 'border-border-strong',
                  ].join(' ')}
                >
                  {isSelected && <span className="h-2 w-2 rounded-full bg-white" />}
                </span>
                <span className="block">
                  <span className="mb-1 block text-base font-bold text-text-primary">{opt.label}</span>
                  <span className="block text-[13px] text-text-secondary">{opt.desc}</span>
                </span>
              </button>
            )
          })}
        </div>

        <button
          data-testid="cert-type-confirm"
          onClick={handleConfirm}
          disabled={loading}
          className="btn-primary w-full"
        >
          {loading ? <Spinner label="設定中..." /> : 'この種別で進む'}
        </button>
      </div>
    </div>
  )
}
